import { lazy } from 'react';

// project imports
import MainLayout from 'layout/MainLayout';
import Loadable from 'ui-component/Loadable';
import PermissionGuard from 'utils/permission-guard';

// dashboard routing
const DashboardDefault = Loadable(lazy(() => import('views/dashboard/Default')));

// sample page routing
const RolesManagement = Loadable(lazy(() => import('views/roles-management')));
const AdminUsers = Loadable(lazy(() => import('views/admin-users')));
const AssignPermissions = Loadable(lazy(() => import('views/roles-management/assign-permissions')));
const Users = Loadable(lazy(() => import('views/users')));
const UserDetails = Loadable(lazy(() => import('views/users/userDetails')));
const Clients = Loadable(lazy(() => import('views/users/clients')));
const RecoveryBoard = Loadable(lazy(() => import('views/recovery-board')));
const PostureLibrary = Loadable(lazy(() => import('views/posture-library')));

// Live (in-person) training — clients trained outside the app
const LiveClients = Loadable(lazy(() => import('views/live-training')));
const LiveClientDetail = Loadable(lazy(() => import('views/live-training/clientDetail')));
const ExpiredClients = Loadable(lazy(() => import('views/users/expired-clients')));
const PermissionsListing = Loadable(lazy(() => import('views/roles-management/permissions-listing')));
const PermissionTypes = Loadable(lazy(() => import('views/roles-management/permission-types-listing')));
const Chat = Loadable(lazy(() => import('views/chat/index')));

// Plan Finder — the post-signup questionnaire
const PlanFinderQuestions = Loadable(lazy(() => import('views/plan-finder')));
const PlanFinderRules = Loadable(lazy(() => import('views/plan-finder/rules')));
const PlanFinderAnalytics = Loadable(lazy(() => import('views/plan-finder/analytics')));

// Marketing — super admin only (see the route guard below)
const WinBack = Loadable(lazy(() => import('views/marketing/win-back')));
const MarketingBroadcast = Loadable(lazy(() => import('views/marketing/broadcast')));
const MarketingFunnel = Loadable(lazy(() => import('views/marketing/funnel')));
const Website = Loadable(lazy(() => import('views/website')));
const MarketingDashboard = Loadable(lazy(() => import('views/marketing/dashboard')));

const ExercisesLibrary = Loadable(lazy(() => import('views/library/exercises')));
const WarmupsLibrary = Loadable(lazy(() => import('views/library/warmups')));
const CoolDownsLibrary = Loadable(lazy(() => import('views/library/cool-downs')));
const FoodsLibrary = Loadable(lazy(() => import('views/library/foods')));

// Content routes

const Categories = Loadable(lazy(() => import('views/content/categories')));
const SubCategories = Loadable(lazy(() => import('views/content/sub-categories')));
const TrainingRequests = Loadable(lazy(() => import('views/trainingRequests')));
const TrainingRequestForm = Loadable(lazy(() => import('views/trainingRequestForm')));
const TrainingRequestFormArabic = Loadable(lazy(() => import('views/trainingRequestForm/index_arabic')));
const Programs = Loadable(lazy(() => import('views/content/programs')));
const Assessments = Loadable(lazy(() => import('views/content/assessments')));

const CustomProgramsListing = Loadable(lazy(() => import('views/content/custom-programs')));
const Levels = Loadable(lazy(() => import('views/content/levels')));
const Intros = Loadable(lazy(() => import('views/content/intros')));
const Weeks = Loadable(lazy(() => import('views/content/weeks')));
const ProgramBuilder = Loadable(lazy(() => import('views/content/builder')));
const CustomProgramBuilder = Loadable(lazy(() => import('views/content/custom-builder')));
const CustomWeeksListing = Loadable(lazy(() => import('views/content/custom-weeks')));
const CustomDaysListing = Loadable(lazy(() => import('views/content/custom-days')));
const CustomBlocksListing = Loadable(lazy(() => import('views/content/custom-blocks')));
const Days = Loadable(lazy(() => import('views/content/days')));
const Blocks = Loadable(lazy(() => import('views/content/blocks')));
const BlockMoves = Loadable(lazy(() => import('views/content/moves')));
const CustomBlockMoves = Loadable(lazy(() => import('views/content/custom-moves')));

const TrainerListing = Loadable(lazy(() => import('views/trainers')));
const NutritionListing = Loadable(lazy(() => import('views/content/nutritions')));
const NutritionWeekListing = Loadable(lazy(() => import('views/content/nutrition-weeks')));
const NutritionDayListing = Loadable(lazy(() => import('views/content/nutrition-days')));
const NutritionBlockListing = Loadable(lazy(() => import('views/content/nutrition-blocks')));

const CustomNutritionListing = Loadable(lazy(() => import('views/content/custom-nutritions')));
const CustomNutritionWeekListing = Loadable(lazy(() => import('views/content/custom-nutrition-weeks')));
const CustomNutritionDayListing = Loadable(lazy(() => import('views/content/custom-nutrition-days')));
const CustomNutritionBlockListing = Loadable(lazy(() => import('views/content/custom-nutrition-blocks')));

const NutritionCalculator = Loadable(lazy(() => import('views/content/nutrition-calculator')));

const MetricCategoryListing = Loadable(lazy(() => import('views/metric-categories')));
const MetricListing = Loadable(lazy(() => import('views/metrics')));
const MetricHistoryListing = Loadable(lazy(() => import('views/metric-history')));
const ProgressListing = Loadable(lazy(() => import('views/progress')));

const NotificationsListing = Loadable(lazy(() => import('views/app-content/notifications')));
const ForceUpdate = Loadable(lazy(() => import('views/app-content/force-update')));
const InAppNotificationsListing = Loadable(lazy(() => import('views/app-content/in-app-notifications')));
const FAQsListing = Loadable(lazy(() => import('views/app-content/faqs')));
const FeedbackListing = Loadable(lazy(() => import('views/app-content/feedback')));
const DayFeedbacks = Loadable(lazy(() => import('views/content/days/feedback')));
const CustomDayFeedbacks = Loadable(lazy(() => import('views/content/custom-days/feedback')));

const Banners = Loadable(lazy(() => import('views/app-content/banners')));
const PrivacyPolicy = Loadable(lazy(() => import('views/app-content/privacy-policy')));
const TermsAndConditions = Loadable(lazy(() => import('views/app-content/terms')));
const AboutUs = Loadable(lazy(() => import('views/app-content/about-us')));
const Subscriptions = Loadable(lazy(() => import('views/app-content/subscriptions')));
const Coupons = Loadable(lazy(() => import('views/app-content/coupons')));
const Transactions = Loadable(lazy(() => import('views/transactions/transactions')));
const UserSubscriptions = Loadable(lazy(() => import('views/transactions/user-subscriptions')));
const InstaPay = Loadable(lazy(() => import('views/transactions/instapay')));
const SiteVisits = Loadable(lazy(() => import('views/site/visits')));
const SiteCampaigns = Loadable(lazy(() => import('views/site/campaigns')));
const SitePayments = Loadable(lazy(() => import('views/site/payments')));
const Profile = Loadable(lazy(() => import('views/profile')));
const LoginHistory = Loadable(lazy(() => import('views/login-history/login-history')));
const ActivityHistory = Loadable(lazy(() => import('views/activity-history/activity-history')));
const DailyQuoteListing = Loadable(lazy(() => import('views/app-content/daily-quotes')));
const MovementPreferencesListing = Loadable(lazy(() => import('views/app-content/movement-preferences')));
const HeadCoachListing = Loadable(lazy(() => import('views/head-coach-users')));
// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
  path: '/',
  element: <MainLayout />,
  children: [
    {
      path: '/',
      element: (
        <PermissionGuard requiredPermissions={['view-dashboard']}>
          <DashboardDefault />
        </PermissionGuard>
      )
    },
    {
      path: 'login-history',
      element: (
        <PermissionGuard requiredPermissions={['view-login-history']}>
          <LoginHistory />
        </PermissionGuard>
      )
    },
    {
      path: 'activity-history',
      element: (
        <PermissionGuard requiredPermissions={['view-activity-history']}>
          <ActivityHistory />
        </PermissionGuard>
      )
    },
    {
      path: 'profile',
      element: (
        <PermissionGuard requiredPermissions={['view-dashboard']}>
          <Profile />
        </PermissionGuard>
      )
    },
    {
      path: 'head-coaches',
      element: (
        <PermissionGuard requiredPermissions={['view-head-coaches']}>
          <HeadCoachListing />
        </PermissionGuard>
      )
    },
    {
      path: 'dashboard',
      element: (
        <PermissionGuard requiredPermissions={['view-dashboard']}>
          <DashboardDefault />
        </PermissionGuard>
      )
    },
    {
      path: 'roles-management',
      element: (
        <PermissionGuard requiredPermissions={['view-roles']}>
          <RolesManagement />
        </PermissionGuard>
      )
    },
    {
      path: 'admin-users',
      element: (
        <PermissionGuard requiredPermissions={['view-admins']}>
          <AdminUsers />
        </PermissionGuard>
      )
    },
    {
      path: 'trainers',
      element: (
        <PermissionGuard requiredPermissions={['view-trainers']}>
          <TrainerListing />
        </PermissionGuard>
      )
    },
    {
      path: 'metrics',
      element: (
        <PermissionGuard requiredPermissions={['view-metrics']}>
          <MetricListing />
        </PermissionGuard>
      )
    },
    {
      path: 'plan-finder',
      element: (
        <PermissionGuard requiredPermissions={['view-program-categories']}>
          <PlanFinderQuestions />
        </PermissionGuard>
      )
    },
    {
      path: 'plan-finder-insights',
      element: (
        <PermissionGuard requiredPermissions={['view-program-categories']}>
          <PlanFinderAnalytics />
        </PermissionGuard>
      )
    },
    {
      path: 'plan-finder-rules',
      element: (
        <PermissionGuard requiredPermissions={['view-program-categories']}>
          <PlanFinderRules />
        </PermissionGuard>
      )
    },
    {
      path: 'metric-categories',
      element: (
        <PermissionGuard requiredPermissions={['view-metric-categories']}>
          <MetricCategoryListing />
        </PermissionGuard>
      )
    },
    {
      path: 'metric-history',
      element: (
        <PermissionGuard requiredPermissions={['view-metric-history']}>
          <MetricHistoryListing />
        </PermissionGuard>
      )
    },
    {
      path: 'progress',
      element: (
        <PermissionGuard requiredPermissions={['view-progress']}>
          <ProgressListing />
        </PermissionGuard>
      )
    },
    {
      path: 'nutritions',
      element: (
        <PermissionGuard requiredPermissions={['view-nutritions']}>
          <NutritionListing />
        </PermissionGuard>
      )
    },
    {
      path: 'nutrition-weeks',
      element: (
        <PermissionGuard requiredPermissions={['view-nutrition-weeks']}>
          <NutritionWeekListing />
        </PermissionGuard>
      )
    },
    {
      path: 'nutrition-days',
      element: (
        <PermissionGuard requiredPermissions={['view-nutrition-days']}>
          <NutritionDayListing />
        </PermissionGuard>
      )
    },
    {
      path: 'nutrition-blocks',
      element: (
        <PermissionGuard requiredPermissions={['view-nutrition-blocks']}>
          <NutritionBlockListing />
        </PermissionGuard>
      )
    },
    {
      path: 'custom-nutritions',
      element: (
        <PermissionGuard requiredPermissions={['view-custom-nutritions']}>
          <CustomNutritionListing />
        </PermissionGuard>
      )
    },
    {
      path: 'custom-nutrition-weeks',
      element: (
        <PermissionGuard requiredPermissions={['view-custom-nutrition-weeks']}>
          <CustomNutritionWeekListing />
        </PermissionGuard>
      )
    },
    {
      path: 'custom-nutrition-days',
      element: (
        <PermissionGuard requiredPermissions={['view-custom-nutrition-days']}>
          <CustomNutritionDayListing />
        </PermissionGuard>
      )
    },
    {
      path: 'custom-nutrition-blocks',
      element: (
        <PermissionGuard requiredPermissions={['view-custom-nutrition-blocks']}>
          <CustomNutritionBlockListing />
        </PermissionGuard>
      )
    },
    {
      path: 'nutrition-calculator',
      element: (
        <PermissionGuard requiredPermissions={['view-nutritions']}>
          <NutritionCalculator />
        </PermissionGuard>
      )
    },
    {
      path: 'assign-permissions',
      element: (
        <PermissionGuard requiredPermissions={['view-roles']}>
          <AssignPermissions />
        </PermissionGuard>
      )
    },
    {
      path: 'users',
      element: (
        <PermissionGuard requiredPermissions={['view-users']}>
          <Users />
        </PermissionGuard>
      )
    },
    {
      path: 'user-details',
      element: (
        <PermissionGuard requiredPermissions={['view-users']} requiredUserTypes={[1, 5]}>
          <UserDetails />
        </PermissionGuard>
      )
    },
    {
      path: 'recovery-board',
      element: (
        <PermissionGuard requiredPermissions={['view-clients']} requiredUserTypes={[1, 5]}>
          <RecoveryBoard />
        </PermissionGuard>
      )
    },
    {
      path: 'live-training',
      element: (
        <PermissionGuard requiredUserTypes={[1, 4, 5]}>
          <LiveClients />
        </PermissionGuard>
      )
    },
    {
      path: 'live-training/:id',
      element: (
        <PermissionGuard requiredUserTypes={[1, 4, 5]}>
          <LiveClientDetail />
        </PermissionGuard>
      )
    },
    {
      path: 'posture-library',
      element: (
        <PermissionGuard requiredPermissions={['view-clients']} requiredUserTypes={[1, 5]}>
          <PostureLibrary />
        </PermissionGuard>
      )
    },
    {
      path: 'clients',
      element: (
        <PermissionGuard requiredPermissions={['view-clients']}>
          <Clients />
        </PermissionGuard>
      )
    },
    {
      path: 'expired-clients',
      element: (
        <PermissionGuard requiredPermissions={['view-clients']}>
          <ExpiredClients />
        </PermissionGuard>
      )
    },
    {
      path: 'permissions',
      element: (
        <PermissionGuard requiredPermissions={['view-roles']}>
          <PermissionsListing />
        </PermissionGuard>
      )
    },
    {
      path: 'permission-types',
      element: (
        <PermissionGuard requiredPermissions={['view-roles']}>
          <PermissionTypes />
        </PermissionGuard>
      )
    },
    {
      path: 'exercises-library',
      element: (
        <PermissionGuard requiredPermissions={['view-library']}>
          <ExercisesLibrary />
        </PermissionGuard>
      )
    },
    {
      path: 'warmups-library',
      element: (
        <PermissionGuard requiredPermissions={['view-library']}>
          <WarmupsLibrary />
        </PermissionGuard>
      )
    },
    {
      path: 'cool-downs-library',
      element: (
        <PermissionGuard requiredPermissions={['view-library']}>
          <CoolDownsLibrary />
        </PermissionGuard>
      )
    },
    {
      path: 'foods-library',
      element: (
        <PermissionGuard requiredPermissions={['view-library']}>
          <FoodsLibrary />
        </PermissionGuard>
      )
    },
    {
      path: 'chat',
      element: (
        <PermissionGuard requiredPermissions={['view-chat']}>
          <Chat />
        </PermissionGuard>
      )
    },

    // Content Routes

    {
      path: 'categories',
      element: (
        <PermissionGuard requiredPermissions={['view-program-categories']}>
          <Categories />
        </PermissionGuard>
      )
    },
    {
      path: 'sub-categories',
      element: (
        <PermissionGuard requiredPermissions={['view-subcategories']}>
          <SubCategories />
        </PermissionGuard>
      )
    },
    {
      path: 'training-request-form-en',
      element: (
        <PermissionGuard requiredPermissions={['view-training-requests']}>
          <TrainingRequestForm />
        </PermissionGuard>
      )
    },
    {
      path: 'training-request-form-arabic',
      element: (
        <PermissionGuard requiredPermissions={['view-training-requests']}>
          <TrainingRequestFormArabic />
        </PermissionGuard>
      )
    },
    {
      path: 'training-requests',
      element: (
        <PermissionGuard requiredPermissions={['view-training-requests']}>
          <TrainingRequests />
        </PermissionGuard>
      )
    },
    {
      path: 'programs',
      element: (
        <PermissionGuard requiredPermissions={['view-programs']}>
          <Programs />
        </PermissionGuard>
      )
    },
    {
      path: 'assessments',
      element: (
        <PermissionGuard requiredPermissions={['view-assessments']}>
          <Assessments />
        </PermissionGuard>
      )
    },
    {
      path: 'custom-programs',
      element: (
        <PermissionGuard requiredPermissions={['view-custom-programs']}>
          <CustomProgramsListing />
        </PermissionGuard>
      )
    },
    {
      path: 'custom-weeks',
      element: (
        <PermissionGuard requiredPermissions={['view-custom-weeks']}>
          <CustomWeeksListing />
        </PermissionGuard>
      )
    },
    {
      path: 'custom-days',
      element: (
        <PermissionGuard requiredPermissions={['view-custom-days']}>
          <CustomDaysListing />
        </PermissionGuard>
      )
    },
    {
      path: 'custom-blocks',
      element: (
        <PermissionGuard requiredPermissions={['view-custom-blocks']}>
          <CustomBlocksListing />
        </PermissionGuard>
      )
    },
    {
      path: 'levels',
      element: (
        <PermissionGuard requiredPermissions={['view-levels']}>
          <Levels />
        </PermissionGuard>
      )
    },
    {
      path: 'weeks',
      element: (
        <PermissionGuard requiredPermissions={['view-weeks']}>
          <Weeks />
        </PermissionGuard>
      )
    },
    {
      path: 'builder',
      element: (
        <PermissionGuard requiredPermissions={['view-weeks']}>
          <ProgramBuilder />
        </PermissionGuard>
      )
    },
    {
      path: 'custom-builder',
      element: (
        <PermissionGuard requiredPermissions={['view-custom-weeks']}>
          <CustomProgramBuilder />
        </PermissionGuard>
      )
    },
    {
      path: 'days',
      element: (
        <PermissionGuard requiredPermissions={['view-days']}>
          <Days />
        </PermissionGuard>
      )
    },
    {
      path: 'feedbacks',
      element: (
        <PermissionGuard requiredPermissions={['view-feedbacks']}>
          <DayFeedbacks />
        </PermissionGuard>
      )
    },
    {
      path: 'custom-feedbacks',
      element: (
        <PermissionGuard requiredPermissions={['view-custom-feedbacks']}>
          <CustomDayFeedbacks />
        </PermissionGuard>
      )
    },
    {
      path: 'blocks',
      element: (
        <PermissionGuard requiredPermissions={['view-blocks']}>
          <Blocks />
        </PermissionGuard>
      )
    },
    {
      path: 'moves',
      element: (
        <PermissionGuard requiredPermissions={['view-moves']}>
          <BlockMoves />
        </PermissionGuard>
      )
    },
    {
      path: 'custom-moves',
      element: (
        <PermissionGuard requiredPermissions={['view-custom-moves']}>
          <CustomBlockMoves />
        </PermissionGuard>
      )
    },
    {
      path: 'intros',
      element: (
        <PermissionGuard requiredPermissions={['view-intros']}>
          <Intros />
        </PermissionGuard>
      )
    },
    {
      path: 'notifications',
      element: (
        <PermissionGuard requiredPermissions={['view-notifications']}>
          <NotificationsListing />
        </PermissionGuard>
      )
    },
    {
      path: 'in-app-notifications',
      element: (
        <PermissionGuard requiredPermissions={['view-in-app-notifications']}>
          <InAppNotificationsListing />
        </PermissionGuard>
      )
    },
    {
      path: 'app-content',
      children: [
        {
          path: 'notifications',
          element: (
            <PermissionGuard requiredPermissions={['view-notifications']}>
              <NotificationsListing />
            </PermissionGuard>
          )
        },
        {
          path: 'force-update',
          element: (
            <PermissionGuard requiredPermissions={['view-notifications']}>
              <ForceUpdate />
            </PermissionGuard>
          )
        },
        {
          path: 'in-app-notifications',
          element: (
            <PermissionGuard requiredPermissions={['view-in-app-notifications']}>
              <InAppNotificationsListing />
            </PermissionGuard>
          )
        },
        {
          path: 'daily-quotes',
          element: (
            <PermissionGuard requiredPermissions={['view-daily-quotes']}>
              <DailyQuoteListing />
            </PermissionGuard>
          )
        },
        {
          path: 'movement-preferences',
          element: (
            <PermissionGuard requiredPermissions={['view-movement-preferences']}>
              <MovementPreferencesListing />
            </PermissionGuard>
          )
        },
        {
          path: 'faqs',
          element: (
            <PermissionGuard requiredPermissions={['view-faqs']}>
              <FAQsListing />
            </PermissionGuard>
          )
        },
        {
          path: 'feedbacks',
          element: (
            <PermissionGuard requiredPermissions={['view-feedbacks']}>
              <FeedbackListing />
            </PermissionGuard>
          )
        },
        {
          path: 'privacy-policy',
          element: (
            <PermissionGuard requiredPermissions={['view-privacy-policy']}>
              <PrivacyPolicy />
            </PermissionGuard>
          )
        },
        {
          path: 'banners',
          element: (
            <PermissionGuard requiredPermissions={['view-banners']}>
              <Banners />
            </PermissionGuard>
          )
        },
        {
          path: 'terms',
          element: (
            <PermissionGuard requiredPermissions={['view-terms-and-conditions']}>
              <TermsAndConditions />
            </PermissionGuard>
          )
        },
        {
          path: 'about-us',
          element: (
            <PermissionGuard requiredPermissions={['view-about-us']}>
              <AboutUs />
            </PermissionGuard>
          )
        },
        {
          path: 'subscription-list',
          element: (
            <PermissionGuard requiredPermissions={['view-subscriptions']}>
              <Subscriptions />
            </PermissionGuard>
          )
        },
        {
          path: 'coupon-list',
          element: (
            <PermissionGuard requiredPermissions={['view-coupons']}>
              <Coupons />
            </PermissionGuard>
          )
        }
      ]
    },
    {
      path: 'transactions',
      element: (
        <PermissionGuard requiredPermissions={['view-transactions']}>
          <Transactions />
        </PermissionGuard>
      )
    },
    {
      path: 'user-subscriptions',
      element: (
        <PermissionGuard requiredPermissions={['view-user-subscriptions']}>
          <UserSubscriptions />
        </PermissionGuard>
      )
    },
    {
      path: 'instapay',
      element: (
        <PermissionGuard requiredPermissions={['view-instapay']}>
          <InstaPay />
        </PermissionGuard>
      )
    },
    {
      path: 'site/visits',
      element: (
        <PermissionGuard requiredPermissions={['view-site-analytics']}>
          <SiteVisits />
        </PermissionGuard>
      )
    },
    {
      path: 'site/campaigns',
      element: (
        <PermissionGuard requiredPermissions={['view-site-analytics']}>
          <SiteCampaigns />
        </PermissionGuard>
      )
    },
    {
      path: 'site/payments',
      element: (
        <PermissionGuard requiredPermissions={['view-site-analytics']}>
          <SitePayments />
        </PermissionGuard>
      )
    },
    {
      // Super admin ONLY (userType 1), not a granular permission: this page sends
      // real email to real clients. The backend enforces the same thing with
      // IsAdminGuard, so hiding the menu item is not the only thing stopping it.
      path: 'marketing/win-back',
      element: (
        <PermissionGuard requiredUserTypes={[1]}>
          <WinBack />
        </PermissionGuard>
      )
    },
    {
      // Super admin ONLY (userType 1), not a granular permission: this page sends
      // real email to real clients. The backend enforces the same thing with
      // IsAdminGuard, so hiding the menu item is not the only thing stopping it.
      path: 'marketing/broadcast',
      element: (
        <PermissionGuard requiredUserTypes={[1]}>
          <MarketingBroadcast />
        </PermissionGuard>
      )
    },
    {
      // Super admin ONLY (userType 1): this page shows revenue and ad spend.
      // The backend enforces the same with IsAdminGuard, so hiding the menu
      // item is not the only thing stopping it.
      path: 'marketing/funnel',
      element: (
        <PermissionGuard requiredUserTypes={[1]}>
          <MarketingFunnel />
        </PermissionGuard>
      )
    },
    {
      // Splat: one screen serves every section, and the tail is the CMS path.
      path: 'website/*',
      element: (
        <PermissionGuard requiredUserTypes={[1]}>
          <Website />
        </PermissionGuard>
      )
    },
    {
      // Super admin ONLY (userType 1): client demographics and revenue-adjacent
      // statistics. Same gate as the rest of Marketing.
      path: 'marketing/dashboard',
      element: (
        <PermissionGuard requiredUserTypes={[1]}>
          <MarketingDashboard />
        </PermissionGuard>
      )
    }
  ]
};

export default MainRoutes;
